import React, { RefObject, useEffect } from 'react';
import { IChartingLibraryWidget } from '../../charting_library';
import { useArchiveDb } from '../../lib/config';
import { captureError } from '../../lib/errorTracking';
import { Gwei } from '../../lib/gwei';
import { tryCatch } from '../../lib/tryCatch';

type SymbolUpdaterProps = {
  widgetRef: RefObject<IChartingLibraryWidget | null>;
  isChartReady: () => boolean;
  doesSymbolMatch: () => boolean;
  symbol: string;
  priceDecimals: number;
  tickSize: bigint;
  onSymbolChangeStart: () => void;
  onSymbolChangeComplete: () => void;
};

/**
 * Handles switching the TradingView chart to a new market/symbol
 * when the product changes
 */
export const SymbolUpdater: React.FC<SymbolUpdaterProps> = ({
  widgetRef,
  isChartReady,
  doesSymbolMatch,
  symbol,
  priceDecimals,
  tickSize,
  onSymbolChangeStart,
  onSymbolChangeComplete,
}) => {
  useEffect(() => {
    if (!isChartReady()) {
      return;
    }

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (doesSymbolMatch() || !chart) {
      return;
    }
    const priceScale = Math.pow(10, priceDecimals);
    const minMove = Gwei.fromGwei(tickSize);
    const frac = false;
    const pythMinTick = `${priceScale},${minMove},${frac}`;
    const minTick = useArchiveDb ? 'default' : pythMinTick;
    // Apply tick size override before switching symbols when using Pyth feed
    tryCatch(() => widgetRef.current?.applyOverrides({ 'mainSeriesProperties.minTick': minTick }));

    // Set the ref to true to prevent lines from being drawn while the chart is switching symbols.
    // The chart's symbol is updated synchronously, but the chart is not fully ready until the callback here is called
    // And then we need the state variable to trigger a re-render to draw the lines after the chart is ready
    onSymbolChangeStart();
    tryCatch(
      () =>
        chart.setSymbol(symbol, () => {
          onSymbolChangeComplete();
        }),
      (error) => {
        captureError(error);
        onSymbolChangeComplete();
      }
    );
  }, [
    isChartReady,
    doesSymbolMatch,
    symbol,
    priceDecimals,
    tickSize,
    onSymbolChangeStart,
    onSymbolChangeComplete,
    widgetRef,
  ]);

  return null;
};
